import { useQuery } from '@tanstack/react-query'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import BillPayAll from 'src/app/BillPayAll/BillPayAll'
import BillPayNeedsApproval from 'src/app/BillPayNeedsApproval/BillPayNeedsApproval'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardScreen'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { Rdrct } from 'src/navigation/Lnk'
import {
  actionUnavailableUrl,
  billPayAllUrl,
  billPayNeedsApprovalUrl,
  billPayUrl,
  createBillPayUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { orgCardApprovalRulesQueries } from 'src/queries/organizations/cardApprovalRules/cardApprovalRules.queries'
import {
  selectBillPayAllMatch,
  selectBillPayNeedsApprovalMatch,
  selectCalculatedBackgroundLocation,
} from 'src/redux/selectors/router.selectors'
import { selectBrandFeaturesFromState } from 'src/redux/selectors/session.selectors'

/**
 * BillPayPage — route-level page for the BillPay family tabs.
 * See src/page-components/AGENTS.md for the family-routing pattern.
 */
const BillPayPage: React.FC = () => {
  const translate = useTranslate()

  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const billPayAllMatch = useSelector(selectBillPayAllMatch)
  const billPayNeedsApprovalMatch = useSelector(selectBillPayNeedsApprovalMatch)
  const user = useCurrentUser()

  const { billPayEnabled } = useSelector(selectBrandFeaturesFromState)
  const {
    VIEW_BILL_PAY_SECTION: viewBillPay,
    CREATE_BILL_PAY_CARD: canCreateBillPayCard,
  } =
    useQueryUserFeatures([
      UserFeature.VIEW_BILL_PAY_SECTION,
      UserFeature.CREATE_BILL_PAY_CARD,
    ]).data || {}
  const showBillPay = viewBillPay && billPayEnabled

  const { data } = useQuery(
    orgCardApprovalRulesQueries.detail(user?.organizationId),
  )
  const userIsApprover = data?.approverId === user?.id

  const subnavOptions = useMemo(
    () => [
      {
        label: translate('All Invoices'),
        to: billPayAllUrl(),
        isActive: billPayAllMatch !== null,
        ariaLabelSource: 'All Invoices',
        'data-testid': 'BillPayAllInvoices',
      },
      ...(userIsApprover
        ? [
            {
              label: translate('Needs Approval'),
              to: billPayNeedsApprovalUrl(),
              isActive: billPayNeedsApprovalMatch !== null,
              ariaLabelSource: 'Needs Approval',
              'data-testid': 'BillPayNeedsApproval',
            },
          ]
        : []),
    ],
    [userIsApprover, billPayAllMatch, billPayNeedsApprovalMatch],
  )

  const buttons = useMemo(
    () => [
      {
        design: ButtonDesigns.designCtaInverse,
        type: ButtonTypes.button,
        text: translate('Pay Multiple Invoices'),
        linkProps: canCreateBillPayCard
          ? {
              to: {
                pathname: createBillPayUrl(),
                search: '',
                state: { screen: CreateVirtualCardScreen.MultipleBillPay },
                hash: '',
              },
              ariaLabelSource: 'Pay Multiple Invoices',
            }
          : {
              to: actionUnavailableUrl(),
              ariaLabel: 'Pay Multiple Invoices',
            },
      },
      {
        design: ButtonDesigns.designCta,
        type: ButtonTypes.button,
        text: translate('Pay Invoice'),
        linkProps: {
          to: canCreateBillPayCard
            ? createBillPayUrl()
            : actionUnavailableUrl(),
          ariaLabel: 'Pay Invoice',
        },
      },
    ],
    [canCreateBillPayCard],
  )

  const isLoading = viewBillPay === undefined

  return isLoading ? (
    <LoadingBackgroundPage />
  ) : showBillPay ? (
    <LayoutShell
      subnavDesign={SubnavDesign.Floating}
      subnavOptions={subnavOptions}
      content={
        <Switch location={calculatedBackgroundLocation}>
          <Route exact path={billPayAllUrl()}>
            <BillPayAll />
          </Route>
          <Route exact path={billPayNeedsApprovalUrl()}>
            <BillPayNeedsApproval />
          </Route>
          <Route exact path={billPayUrl()}>
            <Rdrct to={billPayAllUrl()} />
          </Route>
          <Route path={'/'}>
            <NotFoundBackgroundPageContent />
          </Route>
        </Switch>
      }
      title={translate('Bill Pay Cards')}
      buttons={buttons}
    />
  ) : (
    <NotFoundBackgroundPage />
  )
}

export default BillPayPage
